import { useContext, useState, useCallback, useEffect } from 'react';
import { CAContext } from './src-contexts-CAContext.js';
import { useIdentity } from '@vp/auth-react';
import { transformStartChatApiResponse } from './src-utils-chatbot-api-utils.js';
import { API_ENDPOINTS } from './src-config-api-config.js';
import { ChatbotContext } from './src-contexts-ChatbotContext.js';
import { getRestId, setChatbotToken } from './src-utils-session-storage-utils.js';
import { useNRLogging } from './src-hooks-custom-hooks-useNRLogging.js';
import { getOperatingSystem, getBrowserName, getDeviceType } from './src-utils-app-utils.js';
import { util } from './src-utils-miaw-common.js';
import { DISPLAY_NAME } from './src-config-app-config.js';
import { useCustomTracking } from './src-hooks-custom-hooks-useTracking.js';
import { CaseDataContext } from './src-contexts-CaseDataContext.js';

const useCBChatStart = () => {
  const { logCustomAttributesNR } = useNRLogging();
  const { sendTrackingEvent } = useCustomTracking();
  const identity = useIdentity();
  const {
    chatState: { gcpChatData, userInfo, locale },
    setChatState
  } = useContext(CAContext);
  const {
    state: { pageInfo, caseData }
  } = useContext(CaseDataContext);
  const { setState: setChatbotContext } = useContext(ChatbotContext);
  const { setState: setChatbotState } = useContext(ChatbotContext);
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [toggle, setToggle] = useState(false);
  const trigger = useCallback(() => {
    setToggle((prev) => !prev);
  }, []);
  const { isSignedIn } = identity;
  const t = isSignedIn ? [
    `Hi ${userInfo?.userData?.firstName || ""}! I'm your Vista Assistant. Ask me about your orders, designs, or anything related to using the website. I'll use AI to provide the best answers.`,
    "How can I help you today?"
  ] : [
    `Hi! I'm Vista Assistant. Ask me about your order, design, or anything related to using the website. I'll use AI to provide the best answers.

Tip: You'll get help faster if you log in to your account.

<ext>Sign in</ext>`,
    "How can I help you today?"
  ];
  useEffect(() => {
    const fetchData = async () => {
      if (toggle) {
        try {
          setLoading(true);
          setChatbotContext((prev) => ({
            ...prev,
            conversationMessages: [
              {
                id: util.generateUUID(),
                timestamp: (/* @__PURE__ */ new Date()).getTime(),
                messageContent: t,
                type: "chatbot",
                senderDisplayName: DISPLAY_NAME.CHATBOT,
                isMessageRead: false,
                showFeedback: false
              }
            ]
          }));
          const { authorizationHeader, canonicalId } = identity;
          const myHeaders = new Headers();
          myHeaders.append("Authorization", authorizationHeader || "");
          myHeaders.append("Content-Type", "application/json");
          const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: JSON.stringify({
              shopper_id: canonicalId || "",
              chatLocale: locale,
              pageName: pageInfo?.pageName || "Help Center:Home Page",
              pageURL: window?.location.href,
              caseOrigin: `${caseData?.caseOrigin || ""}-Chatbot`,
              status: caseData?.status || "",
              pageSection: pageInfo?.pageSection || "Help Center",
              pageStage: pageInfo?.pageStage || "Customer Care",
              pagePath: window?.location.pathname,
              isChatEnabled: gcpChatData?.status.isChatEnabled || false,
              userInfo: {
                isLoggedIn: userInfo?.isLoggedIn || false,
                firstName: userInfo?.userData?.firstName || void 0,
                lastName: userInfo?.userData?.lastName || void 0
              },
              request_id: getRestId() || "",
              deviceType: getDeviceType() || "",
              browser: getBrowserName() || "",
              operatingSystem: getOperatingSystem() || ""
            })
          };
          logCustomAttributesNR("ca-chatbot-api-request", {
            logLevel: "info",
            message: "Starting chatbot session",
            apiType: "startChatbotSession"
          });
          const res = await fetch(`${API_ENDPOINTS.CHATBOT_BASE_URL()}/start-session`, requestOptions);
          if (!res.ok) {
            throw new Error("Failed to fetch data");
          }
          const responseData = await res.json();
          const { discussionId, message, sessionId, chatbotToken } = transformStartChatApiResponse(responseData);
          setChatbotToken(chatbotToken, isSignedIn);
          setChatbotState((prev) => ({
            ...prev,
            showHelperButton: true
          }));
          setChatState((prev) => ({
            ...prev,
            chatSession: "chatbotSession"
          }));
          setData({ message, discussionId, sessionId });
          sendTrackingEvent("Chatbot Clicked", "Chatbot:Session", "chatSessionStarted");
          setError(void 0);
          logCustomAttributesNR("ca-chatbot-api-response", {
            logLevel: "info",
            message: "Starting chatbot session API success",
            apiType: "startChatbotSession"
          });
        } catch (err) {
          console.error(err);
          setError(err);
          logCustomAttributesNR("ca-chatbot-api-error", {
            errorName: err.name,
            errorStack: err.stack,
            message: err.message,
            logLevel: "error",
            apiType: "startChatbotSession"
          });
        } finally {
          setLoading(false);
          setToggle(false);
        }
      }
    };
    fetchData();
  }, [toggle]);
  return { data, error, loading, trigger };
};

export { useCBChatStart };
//# sourceMappingURL=src-hooks-chatbot-useCBChatStart.js.map
